import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {ISettingsData} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsView';
import {SettingsViewEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsViewEvents';
import {RecodeSettingsView} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/recode/RecodeSettingsView';

export class GameSettingsView extends RecodeSettingsView {
	protected _dragReelToSpinToggle: Spine;
	protected _dragReelToSpinText: PIXI.Text;

	public setupSettingsViewComponents(previousSettings: ISettingsData): void {
		this.soundActive = previousSettings.soundActive;
		this.musicActive = previousSettings.musicActive;
		this.skipIntroActive = previousSettings.skipIntroActive;
		this.dragReelToSpinActive = previousSettings.dragReelToSpinActive;

		this.setupMainLayout();
		this.setupBackgroundBars();
		this.setupTextStyles();
		this.setupCheckboxes();
		this.setupText();
		this.setupCheckboxesEvents();

		this.translateCustom();

		this.musicActive ? this.emitMusicToggleOn() : this.emitMusicToggleOff();
		this.soundActive ? this.emitSoundToggleOn() : this.emitSoundToggleOff();
		this.skipIntroActive ? this.emitSkipIntroToggleOn() : this.emitSkipIntroToggleOff();
		this.dragReelToSpinActive ? this.emitDragReelsToSpinToggleOn() : this.emitDragReelsToSpinToggleOff();
	}

	protected translateCustom(): void {
		const musicText: string = this.translator.getObject('BaseStrings', 'generic', 'g_music') || 'MUSIC';
		this._musicText.text = musicText.toUpperCase();

		const soundText: string = this.translator.getObject('BaseStrings', 'generic', 'g_soundEffects') || 'SOUND EFFECTS';
		this._soundText.text = soundText.toUpperCase();

		const skipIntroText: string = this.translator.getObject('BaseStrings', 'generic', 'g_skipintro') || 'SKIP INTRO';
		this._skipIntroText.text = skipIntroText.toUpperCase();

		const dragReelToSpinText: string = this.translator.getObject('BaseStrings', 'generic', 'g_dragReelToSpin') || 'DRAG REEL TO SPIN';
		this._dragReelToSpinText.text = dragReelToSpinText.toUpperCase();

		if (dragReelToSpinText.length > 55) {
			this._dragReelToSpinText.style = this._specialDescriptionStyle;

			let baseSize = this._specialDescriptionStyle.fontSize;
			let baseLineHeight = this._specialDescriptionStyle.lineHeight;

			if (typeof baseSize === 'string') {
				baseSize = parseInt(baseSize, 10);
			}

			if (typeof baseLineHeight === 'string') {
				baseLineHeight = parseInt(baseLineHeight, 10);
			}

			if (dragReelToSpinText.length > 100) {
				this._specialDescriptionStyle.fontSize = baseSize * 0.65;
				this._specialDescriptionStyle.lineHeight = baseLineHeight * 0.65;
			} else if (dragReelToSpinText.length > 85) {
				this._specialDescriptionStyle.fontSize = baseSize * 0.75;
				this._specialDescriptionStyle.lineHeight = baseLineHeight * 0.75;
			} else if (dragReelToSpinText.length > 70) {
				this._specialDescriptionStyle.fontSize = baseSize * 0.8;
				this._specialDescriptionStyle.lineHeight = baseLineHeight * 0.8;
			} else if (dragReelToSpinText.length > 55) {
				this._specialDescriptionStyle.fontSize = baseSize * 0.9;
				this._specialDescriptionStyle.lineHeight = baseLineHeight * 0.9;
			}
		}

		this.setDescriptionLineBreak();
	}

	protected setupCheckboxes(): void {
		const yOffset = this._spineLayout.skeleton.findBone('SETTINGS_TITLE_TEXTS').data.y;
		const xOffset = 0;

		const spineName = 'SETTINGS_CONFIG_BUTTON.json';

		this._musicToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_MUSIC', this.musicActive, xOffset, yOffset);
		this._soundToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_SOUNDEFFECTS', this.soundActive, xOffset, yOffset);

		this._dragReelToSpinToggle = this.setupSettingCheckbox(
			spineName,
			'SETTINGS_CONFIG_DRAGTOSPIN',
			this.dragReelToSpinActive,
			xOffset,
			yOffset,
		);

		this._skipIntroToggle = this.setupSettingCheckbox(spineName, 'SETTINGS_CONFIG_SKIPINTRO', this.skipIntroActive, xOffset, yOffset);
	}

	protected setupText(): void {
		const yOffset = this._spineLayout.skeleton.findBone('SETTINGS_TITLE_TEXTS').data.y;

		const musicTextBone = this._spineLayout.skeleton.findSlot('SETTINGS_TEXT_MUSIC').bone.data;
		musicTextBone.x -= 200;

		this._musicText = this.setupSingleText(
			'MUSIC',
			{
				name: 'SETTINGS_TEXT_MUSIC',
				bone: true,
			},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);

		this._soundText = this.setupSingleText(
			'SOUND',
			{name: 'SETTINGS_TEXT_SOUNDEFFECTS', bone: true},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);

		if (this.device.desktop && this.resources.getLocale() === 'de') {
			this._dragReelToSpinText = this.setupSingleText(
				'DRAG REELS TO SPIN',
				{name: 'SETTINGS_TEXT_DRAGTOSPIN', bone: true},
				0,
				yOffset,
				musicTextBone.x,
				null,
				this._specialGermanDesktopsStyle,
			);
		} else {
			this._dragReelToSpinText = this.setupSingleText(
				'DRAG REELS TO SPIN',
				{name: 'SETTINGS_TEXT_DRAGTOSPIN', bone: true},
				0,
				yOffset,
				musicTextBone.x,
				null,
			);
		}

		this._skipIntroText = this.setupSingleText(
			'SKIP INTRO',
			{name: 'SETTINGS_TEXT_SKIPINTRO', bone: true},
			0,
			yOffset,
			musicTextBone.x,
			null,
		);
	}

	protected setupCheckboxesEvents(): void {
		this._musicToggle.interactive = this._musicToggle.buttonMode = true;
		this._soundToggle.interactive = this._soundToggle.buttonMode = true;
		this._skipIntroToggle.interactive = this._skipIntroToggle.buttonMode = true;
		this._dragReelToSpinToggle.interactive = this._dragReelToSpinToggle.buttonMode = true;

		this._musicToggle.on('touchstart', this.onMusicToggle.bind(this));
		this._soundToggle.on('touchstart', this.onSoundToggle.bind(this));
		this._skipIntroToggle.on('touchstart', this.onSkipIntroToggle.bind(this));
		this._dragReelToSpinToggle.on('touchstart', this.onDragReelToSpinToggle.bind(this));
		this._musicToggle.on('mousedown', this.onMusicToggle.bind(this));
		this._soundToggle.on('mousedown', this.onSoundToggle.bind(this));
		this._skipIntroToggle.on('mousedown', this.onSkipIntroToggle.bind(this));
		this._dragReelToSpinToggle.on('mousedown', this.onDragReelToSpinToggle.bind(this));
	}

	protected onDragReelToSpinToggle(): void {
		this._dragReelToSpinActive = this.toggleItem(
			this._dragReelToSpinActive,
			this._dragReelToSpinToggle,
			this.emitDragReelsToSpinToggleOn.bind(this),
			this.emitDragReelsToSpinToggleOff.bind(this),
		);
	}

	protected emitDragReelsToSpinToggleOn(): void {
		this.emit(SettingsViewEvents.TOGGLE_DRAG_REEL_TO_SPIN_ON);
	}

	protected emitDragReelsToSpinToggleOff(): void {
		this.emit(SettingsViewEvents.TOGGLE_DRAG_REEL_TO_SPIN_OFF);
	}

	public set dragReelToSpinActive(value: boolean) {
		this._dragReelToSpinActive = value;
	}

	public get dragReelToSpinActive(): boolean {
		return this._dragReelToSpinActive;
	}

	public hideDragReelOption(): void {
		this._dragReelToSpinText.visible = false;
		this._dragReelToSpinToggle.visible = false;
	}
}



// WEBPACK FOOTER //
// ./src/mainmenu/settingsmenu/GameSettingsView.ts